/**
 * Мелкие чистые функции подбора: категории и ключи справочников.
 *
 * Пороги категорий продублированы с backend **осознанно и только как запасной
 * вариант**: категория приходит в ответе посчитанной, но у старых прогонов поле
 * может быть пустым, а показывать число без словесной категории нельзя — цвет
 * никогда не единственный носитель смысла (CLAUDE.md §8).
 */

import { matchCategory } from '@shared/ui/ScoreRing/matchCategory';

import type {
  ConfidenceCategory,
  ConfidenceFactors,
  MarketSnapshot,
  SearchRunStage,
  SearchRunState,
} from './types';

/** MATCHING §8.4: `≥ 80 high`, `60–79.99 medium`, `< 60 low`. */
export function confidenceCategory(score: number): ConfidenceCategory {
  if (score >= 80) return 'high';
  if (score >= 60) return 'medium';
  return 'low';
}

export const confidenceKey = (category: ConfidenceCategory): string =>
  `results.confidence.${category}`;

export const matchCategoryKey = (category: string): string => `results.category.${category}`;

/** Нижняя граница показываемого Match Score (решение заказчика от 28.10.2026). */
export const SHOWN_MATCH_SCORE_MIN = 80;

/**
 * Ниже этого балла автомобиль в рабочий набор не попадает
 * (`TEASER_MIN_MATCH_SCORE`), поэтому шкала показа начинается отсюда.
 */
const SHOWN_MATCH_SCORE_FLOOR = 55;

/**
 * Match Score так, как его видит пользователь: всегда 80–100 (FE-595).
 *
 * Решение заказчика от 28.10.2026: «69» на карточке отталкивает, хотя машина
 * прошла все обязательные условия и попала в набор. Число на экране — это
 * **отображение**, а не оценка: backend, сортировка, отбор четвёрки и
 * разбивка по компонентам работают с настоящим баллом. Отображение монотонно —
 * машина с бо́льшим баллом никогда не показана ниже соседки. Шкала линейная:
 * порог набора (55) становится 80, 100 остаётся 100; всё ниже порога — 80.
 */
export function shownMatchScore(score: number): number {
  const bounded = Math.max(0, Math.min(100, score));
  if (bounded <= SHOWN_MATCH_SCORE_FLOOR) return SHOWN_MATCH_SCORE_MIN;
  const span = (100 - SHOWN_MATCH_SCORE_MIN) / (100 - SHOWN_MATCH_SCORE_FLOOR);
  return Math.round(SHOWN_MATCH_SCORE_MIN + (bounded - SHOWN_MATCH_SCORE_FLOOR) * span);
}

/**
 * Ключ словесной категории рядом с показанным числом.
 *
 * Категория считается **от показанного** балла, а не берётся из ответа:
 * «86 · Teilweise passend» спорило бы само с собой.
 */
export const shownMatchCategoryKey = (score: number): string =>
  matchCategoryKey(matchCategory(shownMatchScore(score)));

export const stageKey = (stage: SearchRunStage): string => `results.progress.stage.${stage}`;

/** Прогон ещё выполняется: показывается экран ожидания, а не список. */
export function isRunPending(run: SearchRunState): boolean {
  return run.status === 'queued' || run.status === 'processing';
}

/** У прогона есть результаты, которые можно читать (BE-087 отдаёт 409 у остальных). */
export function isRunReadable(run: SearchRunState): boolean {
  return run.status === 'completed' || run.status === 'stale';
}

/**
 * Есть ли разбор уверенности.
 *
 * Пустой объект — прогон без сохранённого разбора: показывать модалку «Как
 * рассчитано» в этом случае нечем, и кнопка не появляется. Придумывать факторы
 * по итоговому числу нельзя (MVP_PRD §17).
 */
export function hasConfidenceFactors(
  value: ConfidenceFactors | Record<string, never>,
): value is ConfidenceFactors {
  return 'data' in value && 'match' in value;
}

/**
 * Отклонение от медианы для карточки результата.
 *
 * `null` — числа показывать нельзя: сравнение не рассчитывалось, выборки не
 * нашлось или снимок старше 90 дней (MVP_PRD §16). Отсутствие числа не
 * заменяется нулём и не прячется молча — карточка показывает «нет сравнения».
 */
export function marketDeltaPercent(market: MarketSnapshot | null): number | null {
  if (market === null) return null;
  if (market.status !== 'available' && market.status !== 'stale') return null;
  return market.delta_percent;
}
